import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import axiosInstance from './api/axios';
import Header from './Header';
import { useAuth } from './AuthContext';
import { FaArrowLeft, FaMinus, FaPlus } from 'react-icons/fa';

// --- HELPER FUNCTIONS --- 

const parseDateUTC = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length === 3) {
        return new Date(Date.UTC(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10)));
    }
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    }
    console.warn("Could not parse date string reliably:", dateString);
    return null;
};

const daysBetween = (date1, date2) => {
    if (!date1 || !date2 || isNaN(date1.getTime()) || isNaN(date2.getTime())) return 0;
    const oneDay = 1000 * 60 * 60 * 24;
    const date1UTC = Date.UTC(date1.getUTCFullYear(), date1.getUTCMonth(), date1.getUTCDate());
    const date2UTC = Date.UTC(date2.getUTCFullYear(), date2.getUTCMonth(), date2.getUTCDate());
    return Math.round((date2UTC - date1UTC) / oneDay);
};

const getDaysInMonthUTC = (year, month) => {
    return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
};

// --- COLOR & STATUS LOGIC --- 
const COLORS = {
    RED: '#ef4444',
    YELLOW: '#fcd34d',
    GREEN: '#48bb78',
    BLUE: '#60a5fa',
    DROP_SUMMARY: '#9ca3af',
    INTERFACE_SUMMARY: '#a78bfa',
};

const getTaskStatusInfo = (task) => {
    const status = task.status || "Not Started";
    const isCompleted = status === "Completed";

    const endDate = parseDateUTC(task.end_date);
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    if (endDate && daysBetween(today, endDate) < 0 && !isCompleted) {
        return { color: COLORS.RED, text: 'Delayed' };
    }
    if (isCompleted) {
        return { color: COLORS.GREEN, text: 'Completed' };
    }
    if (status === "In Progress") {
        return { color: COLORS.YELLOW, text: 'In Progress' };
    }
    return { color: COLORS.BLUE, text: 'Not Started' };
};

// --- CHILD COMPONENTS --- 

const TaskLegend = () => (
    <div className="task-legend">
        <div className="legend-item"><span className="legend-color-box" style={{ backgroundColor: COLORS.BLUE }}></span>Task (Not Started)</div>
        <div className="legend-item"><span className="legend-color-box" style={{ backgroundColor: COLORS.YELLOW }}></span>Task (In Progress)</div>
        <div className="legend-item"><span className="legend-color-box" style={{ backgroundColor: COLORS.GREEN }}></span>Task (Completed)</div>
        <div className="legend-item"><span className="legend-color-box" style={{ backgroundColor: COLORS.RED }}></span>Task (Delayed)</div>
    </div>
);

const HistoryModal = ({ isOpen, onClose, history }) => {
    if (!isOpen) return null;
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Recent Schedule Changes</h3>
                    <button onClick={onClose} className="modal-close-button">&times;</button>
                </div>
                <div className="modal-body">
                    {history.length > 0 ? (
                        <ul className="history-list">
                            {history.map(log => (
                                <li key={log.id} className="history-item">
                                    <div className="history-meta">
                                        <span className="history-user">{log.user_username}</span>
                                        <span className="history-timestamp">{new Date(log.timestamp).toLocaleString()}</span>
                                    </div>
                                    <p className="history-description">{log.description}</p>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="info-message">No recent changes found for this project.</p>
                    )}
                </div>
                <div className="modal-footer">
                    <button onClick={onClose} className="action-button secondary">Close</button>
                </div>
            </div>
        </div>
    );
};

// --- MAIN PAGE COMPONENT ---
export default function DeadlineSchedulePage() {
    const navigate = useNavigate();

    const { user, selectedProjectName } = useAuth();
    const userProjects = user?.projects || [];

    const [schedule, setSchedule] = useState([]);
    const [history, setHistory] = useState([]);
    const [collapsedDrops, setCollapsedDrops] = useState({});
    const [minimizedInterfaces, setMinimizedInterfaces] = useState({});
    const [timelineView, setTimelineView] = useState('days');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

    const [selectedInterfaces, setSelectedInterfaces] = useState([]);
    const [selectedTasks, setSelectedTasks] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);

    const ganttChartRef = useRef(null);
    const [isExporting, setIsExporting] = useState(false);
    const WYSUPP_TASKS = ["Technical Validation", "Upload in RELEX", "Connect Integration"];

    const toggleDropCollapse = (dropId) => {
        setCollapsedDrops(prev => ({ ...prev, [dropId]: !prev[dropId] }));
    };

    const toggleInterfaceMinimize = (interfaceName) => {
        setMinimizedInterfaces(prev => {
            const newState = { ...prev };
            if (newState[interfaceName]) {
                delete newState[interfaceName];
            } else {
                newState[interfaceName] = true;
            }
            return newState;
        });
    };

    useEffect(() => {
        if (!selectedProjectName) {
            setSchedule([]); setHistory([]);
            if (user) { setLoading(false); } else { setLoading(true); }
            return;
        }
        const fetchAllData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [scheduleRes, historyRes] = await Promise.all([
                    axiosInstance.get(`/api/projects/${selectedProjectName}/schedule`),
                    axiosInstance.get(`/api/projects/${selectedProjectName}/schedule/history`)
                ]);
                setSchedule(scheduleRes.data);
                setHistory(historyRes.data);
            } catch (err) {
                if (err.response?.status === 401) {
                    return;
                }
                setError(err.response?.data?.detail || `Failed to load schedule data for ${selectedProjectName}.`);
                setSchedule([]);
                setHistory([]);
            } finally {
                setLoading(false);
            }
        };
        if (user) { fetchAllData(); }
    }, [selectedProjectName, user]);

    const filterOptions = useMemo(() => {
        if (!schedule || schedule.length === 0) return { tasks: [], interfaces: [] };
        const allTasks = schedule.flatMap(d => d.tasks);
        const uniqueTaskNames = [...new Set(allTasks.map(t => t.task_name))];
        const uniqueInterfaceNames = [...new Set(allTasks.map(t => t.interface_name))];
        return {
            tasks: uniqueTaskNames.sort(),
            interfaces: uniqueInterfaceNames.sort().map(name => ({
                key: name,
                label: name.replace(/_/g, ' ')
            })),
        };
    }, [schedule]);

    const statusOptions = ['Not Started', 'In Progress', 'Completed', 'Delayed'];

    const processedSchedule = useMemo(() => {
        if (!schedule || schedule.length === 0) return [];

        const calculateSummaryColor = (taskStatusInfos) => {
            if (taskStatusInfos.some(s => s.text === 'Delayed')) return COLORS.RED;
            if (taskStatusInfos.every(s => s.text === 'Completed')) return COLORS.GREEN;
            if (taskStatusInfos.some(s => s.text === 'In Progress' || s.text === 'Completed')) return COLORS.YELLOW;
            return COLORS.BLUE;
        };

        return schedule.map(drop => {
            const filteredTasks = drop.tasks.filter(task => {
                const interfaceMatch = selectedInterfaces.length === 0 || selectedInterfaces.includes(task.interface_name);
                const taskMatch = selectedTasks.length === 0 || selectedTasks.includes(task.task_name);
                const taskStatusInfo = getTaskStatusInfo(task);
                const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(taskStatusInfo.text);
                return interfaceMatch && taskMatch && statusMatch;
            });

            const interfacesMap = new Map();
            filteredTasks.forEach(task => {
                if (!interfacesMap.has(task.interface_name)) {
                    interfacesMap.set(task.interface_name, { tasks: [], dates: [] });
                }
                interfacesMap.get(task.interface_name).tasks.push(task);
                const startDate = parseDateUTC(task.start_date);
                const endDate = parseDateUTC(task.end_date);
                if (startDate) interfacesMap.get(task.interface_name).dates.push(startDate);
                if (endDate) interfacesMap.get(task.interface_name).dates.push(endDate);
            });

            const interfacesWithSummaries = Array.from(interfacesMap.entries()).map(([interfaceName, data]) => {
                const { tasks, dates } = data;

                // --- FIX APPLIED HERE: Enforce strict task order ---
                const TASK_ORDER = ["Data Delivery", "Technical Validation", "Upload in RELEX", "Functional Validation", "Connect Integration"];
                tasks.sort((a, b) => {
                    const indexA = TASK_ORDER.indexOf(a.task_name);
                    const indexB = TASK_ORDER.indexOf(b.task_name);
                    
                    // If a task isn't in the list, push it to the very bottom instead of breaking the order
                    const orderA = indexA !== -1 ? indexA : 999;
                    const orderB = indexB !== -1 ? indexB : 999;
                    
                    return orderA - orderB;
                });
                // ---------------------------------------------------

                const interfaceStartDate = dates.length > 0 ? new Date(Math.min(...dates.map(d => d.getTime()))) : null;
                const interfaceEndDate = dates.length > 0 ? new Date(Math.max(...dates.map(d => d.getTime()))) : null;

                const taskStatusInfos = tasks.map(task => getTaskStatusInfo(task));
                const summaryColor = calculateSummaryColor(taskStatusInfos);

                return { interfaceName, tasks, interfaceStartDate, interfaceEndDate, summaryColor };
            });

            const allDropDates = interfacesWithSummaries.flatMap(i => [i.interfaceStartDate, i.interfaceEndDate]).filter(Boolean).map(d => d.getTime());
            const dropStartDate = allDropDates.length > 0 ? new Date(Math.min(...allDropDates)) : null;
            const dropEndDate = allDropDates.length > 0 ? new Date(Math.max(...allDropDates)) : null;

            const interfaceSummaryStatusInfos = interfacesWithSummaries.map(iface => {
                if (iface.summaryColor === COLORS.RED) return { text: 'Delayed' };
                if (iface.summaryColor === COLORS.GREEN) return { text: 'Completed' };
                if (iface.summaryColor === COLORS.YELLOW) return { text: 'In Progress' };
                return { text: 'Not Started' };
            });
            const dropSummaryColor = calculateSummaryColor(interfaceSummaryStatusInfos);

            return { ...drop, interfaces: interfacesWithSummaries, dropStartDate, dropEndDate, dropSummaryColor };

        }).filter(drop => drop.interfaces.length > 0);

    }, [schedule, selectedInterfaces, selectedTasks, selectedStatuses]);

    const { dateHeaders, totalUnits, ganttStartDate, unitDuration, timelineGridStyle } = useMemo(() => {
        const defaultReturn = { dateHeaders: [], totalUnits: 0, ganttStartDate: null, unitDuration: 1, timelineGridStyle: {} };
        if (!processedSchedule || processedSchedule.length === 0) return defaultReturn;

        const allDates = processedSchedule.flatMap(drop =>
            drop.interfaces.flatMap(iface =>
                iface.tasks.flatMap(task => [parseDateUTC(task.start_date), parseDateUTC(task.end_date)])
            )
        ).filter(d => d && !isNaN(d.getTime()));

        if (allDates.length === 0) {
            const allScheduleDates = schedule.flatMap(drop =>
                drop.tasks.flatMap(task => [parseDateUTC(task.start_date), parseDateUTC(task.end_date)])
            ).filter(d => d && !isNaN(d.getTime()));
            if (allScheduleDates.length === 0) return defaultReturn;
            allDates.push(...allScheduleDates);
        }

        const minTimestamp = Math.min(...allDates.map(d => d.getTime()));
        const maxTimestamp = Math.max(...allDates.map(d => d.getTime()));
        let minDate = new Date(minTimestamp);
        let maxDate = new Date(maxTimestamp);

        if (minDate.getTime() === maxDate.getTime()) {
            minDate.setUTCDate(minDate.getUTCDate() - 1);
            maxDate.setUTCDate(maxDate.getUTCDate() + 1);
        }

        if (timelineView === 'days') {
            const paddedMinDate = new Date(minDate);
            paddedMinDate.setUTCDate(paddedMinDate.getUTCDate() - 2);
            const paddedMaxDate = new Date(maxDate);
            paddedMaxDate.setUTCDate(paddedMaxDate.getUTCDate() + 3);

            const totalDaysCount = daysBetween(paddedMinDate, paddedMaxDate) + 1;
            if (totalDaysCount <= 0 || totalDaysCount > 500) return defaultReturn;

            const headers = Array.from({ length: totalDaysCount }, (_, i) => {
                const date = new Date(paddedMinDate);
                date.setUTCDate(date.getUTCDate() + i);
                return { type: 'day', date };
            });

            const gridTemplateColumns = `repeat(${totalDaysCount}, 1fr)`;
            const totalMinWidth = totalDaysCount * 45;

            return {
                dateHeaders: headers,
                totalUnits: totalDaysCount,
                ganttStartDate: paddedMinDate,
                unitDuration: 1,
                timelineGridStyle: { gridTemplateColumns }
            };
        } else if (timelineView === 'weeks') {
            const startOfWeek = new Date(minDate);
            startOfWeek.setUTCDate(startOfWeek.getUTCDate() - startOfWeek.getUTCDay());
            const endOfLastWeek = new Date(maxDate);
            endOfLastWeek.setUTCDate(endOfLastWeek.getUTCDate() + (6 - endOfLastWeek.getUTCDay()));

            const headers = [];
            let current = new Date(startOfWeek);
            while (current <= endOfLastWeek) {
                const weekEndDate = new Date(current);
                weekEndDate.setUTCDate(weekEndDate.getUTCDate() + 6);
                headers.push({ type: 'week', date: new Date(current), endDate: weekEndDate });
                current.setUTCDate(current.getUTCDate() + 7);
            }

            const totalDaysInView = daysBetween(startOfWeek, endOfLastWeek) + 1;
            if (totalDaysInView <= 0) return defaultReturn;

            const numWeeks = headers.length;
            const minCellWidth = 100;
            const totalMinWidth = numWeeks * minCellWidth;
            const gridTemplateColumns = headers.map(() => `minmax(${minCellWidth}px, 1fr)`).join(' ');


            return {
                dateHeaders: headers,
                totalUnits: totalDaysInView,
                ganttStartDate: startOfWeek,
                unitDuration: 1,
                timelineGridStyle: { gridTemplateColumns, minWidth: `${totalMinWidth}px` }
            };
        } else {
            const startOfMonth = new Date(Date.UTC(minDate.getUTCFullYear(), minDate.getUTCMonth(), 1));
            const endOfLastMonth = new Date(Date.UTC(maxDate.getUTCFullYear(), maxDate.getUTCMonth() + 1, 0));

            const headers = [];
            let currentMonth = new Date(startOfMonth);
            while (currentMonth <= endOfLastMonth) {
                headers.push({
                    type: 'month',
                    date: new Date(currentMonth),
                    daysInMonth: getDaysInMonthUTC(currentMonth.getUTCFullYear(), currentMonth.getUTCMonth())
                });
                currentMonth.setUTCMonth(currentMonth.getUTCMonth() + 1);
            }

            const totalDaysInView = daysBetween(startOfMonth, endOfLastMonth) + 1;
            if (totalDaysInView <= 0) return defaultReturn;

            const numMonths = headers.length;
            const minMonthWidth = 150;
            const totalMinWidth = numMonths * minMonthWidth;
            const gridTemplateColumns = headers.map(h => `minmax(${minMonthWidth}px, ${h.daysInMonth}fr)`).join(' ');

            return {
                dateHeaders: headers,
                totalUnits: totalDaysInView,
                ganttStartDate: startOfMonth,
                unitDuration: 1,
                timelineGridStyle: { gridTemplateColumns, minWidth: `${totalMinWidth}px` }
            };
        }
    }, [processedSchedule, timelineView, schedule]);

    const handleResetFilters = () => {
        setSelectedInterfaces([]);
        setSelectedTasks([]);
        setSelectedStatuses([]);
    };

    const handleExportCSV = () => {
        if (processedSchedule.length === 0) { alert("No data available to export."); return; }
        const rowsToExport = [];
        processedSchedule.forEach(drop => { drop.interfaces.forEach(iface => { iface.tasks.forEach(task => { rowsToExport.push({ "Drop": drop.name, "Interface": iface.interfaceName.replace(/_/g, ' '), "Task": task.task_name, "Status": getTaskStatusInfo(task).text, "Start Date": task.start_date || 'N/A', "End Date": task.end_date || 'N/A', "Responsible Users": task.responsible_users.map(u => u.username).join('; ') || 'Unassigned' }); }); }); });
        if (rowsToExport.length === 0) { alert("The current filters result in no tasks to export."); return; }
        const csvHeader = Object.keys(rowsToExport[0]).join(',');
        const csvRows = rowsToExport.map(row => Object.values(row).map(value => `"${String(value).replace(/"/g, '""')}"`).join(','));
        const csvContent = [csvHeader, ...csvRows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }); const link = document.createElement('a'); const url = URL.createObjectURL(blob); link.setAttribute('href', url); link.setAttribute('download', `${selectedProjectName}_schedule_export.csv`); link.style.visibility = 'hidden'; document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };

    const handleExportPNG = async () => {
        if (!ganttChartRef.current) { alert("Chart element not found."); return; }
        setIsExporting(true);
        try {
            const element = ganttChartRef.current;
            const originalOverflow = element.style.overflow;
            element.style.overflow = 'visible';

            const canvas = await html2canvas(element, {
                backgroundColor: '#1a202c', scale: 2,
                width: element.scrollWidth, height: element.scrollHeight,
                scrollX: 0, scrollY: 0,
                windowWidth: element.scrollWidth, windowHeight: element.scrollHeight
            });

            element.style.overflow = originalOverflow;

            const dataUrl = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.href = dataUrl;
            link.setAttribute('download', `${selectedProjectName}_schedule.png`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error("Error exporting PNG:", err);
            alert("An error occurred while exporting the chart as a PNG.");
        } finally {
            setIsExporting(false);
        }
    };

    const filtersAreActive = selectedInterfaces.length > 0 || selectedTasks.length > 0 || selectedStatuses.length > 0;

    // --- NEW FILTER TOGGLE LOGIC ---
    const handleFilterToggle = (setter, value) => {
        setter(prevFilters => {
            if (prevFilters.includes(value)) {
                return prevFilters.filter(item => item !== value);
            } else {
                return [...prevFilters, value];
            }
        });
    };

    if (!user) return <div className="loading-container"><div className="loading-spinner"></div><p>Loading user data...</p></div>;

    return (
        <div className="admin-page-container">
            <Header />

            <main className="admin-main-content">
                <div className="page-header">
                    <button onClick={() => navigate('/home')} className="back-button">
                        <FaArrowLeft /> Back
                    </button>

                    <div className="header-title-group">
                        <h1 className="page-title">Project Schedule</h1>
                        {selectedProjectName && (
                            <div className="project-context-inline">
                                <span className="separator">|</span>
                                <span className="context-label">Project:</span>
                                <span className="project-highlight">{selectedProjectName}</span>
                            </div>
                        )}
                    </div>

                    <div className="page-header-actions">
                        {selectedProjectName && (
                            <button onClick={handleExportCSV} className="action-button secondary" disabled={isExporting}> Export CSV </button>
                        )}
                        {selectedProjectName && (
                            <button onClick={handleExportPNG} className="action-button secondary" disabled={isExporting}> {isExporting ? 'Exporting...' : 'Export PNG'} </button>
                        )}
                        {selectedProjectName && (
                            <button onClick={() => setIsHistoryModalOpen(true)} className="action-button secondary" disabled={isExporting}> View History </button>
                        )}
                    </div>
                </div>

                {selectedProjectName && (
                    <div className="filter-controls-wrapper content-card">

                        {/* --- UPDATED FILTERS START --- */}
                        <div className="filter-group">
                            <label htmlFor="interface-filter">Filter by Interface</label>
                            <select id="interface-filter" multiple value={selectedInterfaces} readOnly>
                                {filterOptions.interfaces.map(opt => (
                                    <option
                                        key={opt.key}
                                        value={opt.key}
                                        // --- ADD THIS LINE ---
                                        className={selectedInterfaces.includes(opt.key) ? 'selected-filter-option' : ''}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleFilterToggle(setSelectedInterfaces, opt.key);
                                        }}
                                    >
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label htmlFor="task-filter">Filter by Task</label>
                            <select id="task-filter" multiple value={selectedTasks} readOnly>
                                {filterOptions.tasks.map(opt => (
                                    <option
                                        key={opt}
                                        value={opt}
                                        // --- ADD THIS LINE ---
                                        className={selectedTasks.includes(opt) ? 'selected-filter-option' : ''}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleFilterToggle(setSelectedTasks, opt);
                                        }}
                                    >
                                        {opt}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label htmlFor="status-filter">Filter by Status</label>
                            <select id="status-filter" multiple value={selectedStatuses} readOnly>
                                {statusOptions.map(opt => (
                                    <option
                                        key={opt}
                                        value={opt}
                                        // --- ADD THIS LINE ---
                                        className={selectedStatuses.includes(opt) ? 'selected-filter-option' : ''}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleFilterToggle(setSelectedStatuses, opt);
                                        }}
                                    >
                                        {opt}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {/* --- UPDATED FILTERS END --- */}

                        <div className="view-toggle">
                            <button onClick={() => setTimelineView('days')} className={timelineView === 'days' ? 'active' : ''}>Day</button>
                            <button onClick={() => setTimelineView('weeks')} className={timelineView === 'weeks' ? 'active' : ''}>Week</button>
                            <button onClick={() => setTimelineView('months')} className={timelineView === 'months' ? 'active' : ''}>Month</button>
                        </div>
                        {filtersAreActive && (
                            <div className="filter-group">
                                <button onClick={handleResetFilters} className="reset-filters-button action-button danger">Reset Filters</button>
                            </div>
                        )}
                    </div>
                )}

                {loading && <div className="loading-container"><div className="loading-spinner"></div><p>Loading schedule...</p></div>}
                {error && <div className="form-feedback error">{error}</div>}
                {!loading && !error && !selectedProjectName && userProjects.length > 0 && (
                    <p className="info-message">Please select a project from the header to view its schedule.</p>
                )}
                {!loading && !error && !selectedProjectName && userProjects.length === 0 && (
                    <p className="info-message">You are not assigned to any projects.</p>
                )}
                {!loading && !error && selectedProjectName && (
                    <>
                        {processedSchedule.length === 0 ? (
                            <p className="info-message">
                                {filtersAreActive ? "No tasks match the current filter criteria." : `No schedule defined for "${selectedProjectName}".`}
                            </p>
                        ) : totalUnits > 0 ? (
                            <div className="gantt-main-content content-card">
                                <div className="gantt-container" ref={ganttChartRef}>
                                    <div className="gantt-chart" style={{ '--total-units': totalUnits }}>
                                        <div className="gantt-labels">
                                            <div className="gantt-header-row">
                                                <div className="gantt-header-cell">Task</div>
                                                <div className="gantt-header-cell responsible-header">Responsible</div>
                                            </div>
                                            {processedSchedule.map(drop => {
                                                const isCollapsed = collapsedDrops[drop.id];
                                                return (
                                                    <React.Fragment key={drop.id}>
                                                        <div className="gantt-drop-header-row" onClick={() => toggleDropCollapse(drop.id)}>
                                                            <div className="gantt-drop-header">
                                                                <span className={`collapse-arrow ${isCollapsed ? 'collapsed' : ''}`}>&#9660;</span>
                                                                {drop.name}
                                                            </div>
                                                        </div>
                                                        {!isCollapsed && drop.interfaces.map(iface => (
                                                            <React.Fragment key={iface.interfaceName}>
                                                                <div className="gantt-interface-header-row">
                                                                    <div className="gantt-interface-header">
                                                                        <div className="interface-header-content">
                                                                            <span>{iface.interfaceName.replace(/_/g, ' ')}</span>
                                                                            <button
                                                                                className="minimize-interface-btn"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    toggleInterfaceMinimize(iface.interfaceName);
                                                                                }}
                                                                                title={`${minimizedInterfaces[iface.interfaceName] ? 'Expand' : 'Minimize'} ${iface.interfaceName}`}
                                                                            >
                                                                                {minimizedInterfaces[iface.interfaceName] ? <FaPlus /> : <FaMinus />}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                {!minimizedInterfaces[iface.interfaceName] && iface.tasks.map(task => {
                                                                    let displayResponsible = WYSUPP_TASKS.includes(task.task_name) ? "Wysupp" : selectedProjectName;
                                                                    return (
                                                                        <div key={task.id} className="gantt-task-row-labels">
                                                                            <div className="gantt-task-label">
                                                                                <span className="task-name-detail">{task.task_name}</span>
                                                                            </div>
                                                                            <div className="gantt-responsible-label">
                                                                                <span className="task-responsible-text">{displayResponsible}</span>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </React.Fragment>
                                                        ))}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                        <div className="gantt-timeline" data-view={timelineView} style={{ minWidth: timelineGridStyle.minWidth }}>
                                            <div className="gantt-header-timeline" style={timelineGridStyle}>
                                                {dateHeaders.map((header, i) => (
                                                    <div key={i} className="gantt-date-cell">
                                                        {header.type === 'day' ? (
                                                            <><span className="date-month">{header.date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })}</span><span className="date-day">{header.date.getUTCDate()}</span></>
                                                        ) : header.type === 'week' ? (
                                                            <span className="week-label">{`${header.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })} - ${header.endDate.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'UTC' })}`}</span>
                                                        ) : (
                                                            <span className="month-label">{header.date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })}</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="gantt-body">
                                                {processedSchedule.map(drop => {
                                                    const isCollapsed = collapsedDrops[drop.id];
                                                    return (
                                                        <React.Fragment key={drop.id}>
                                                            <div className="gantt-row gantt-drop-separator">
                                                                {drop.dropStartDate && drop.dropEndDate && (<div className="gantt-summary-bar gantt-drop-bar" style={{ backgroundColor: drop.dropSummaryColor, '--start-offset': daysBetween(ganttStartDate, drop.dropStartDate) / unitDuration, '--duration': (daysBetween(drop.dropStartDate, drop.dropEndDate) + 1) / unitDuration }}></div>)}
                                                            </div>
                                                            {!isCollapsed && drop.interfaces.map(iface => (
                                                                <React.Fragment key={iface.interfaceName}>
                                                                    <div className="gantt-row gantt-interface-separator">
                                                                        {iface.interfaceStartDate && iface.interfaceEndDate && (<div className="gantt-summary-bar gantt-interface-bar" style={{ backgroundColor: iface.summaryColor, '--start-offset': daysBetween(ganttStartDate, iface.interfaceStartDate) / unitDuration, '--duration': (daysBetween(iface.interfaceStartDate, iface.interfaceEndDate) + 1) / unitDuration }}></div>)}
                                                                    </div>
                                                                    {!minimizedInterfaces[iface.interfaceName] && iface.tasks.map(task => {
                                                                        const taskStartDate = parseDateUTC(task.start_date);
                                                                        const taskEndDate = parseDateUTC(task.end_date);
                                                                        if (!taskStartDate || !taskEndDate) return <div key={task.id} className="gantt-task-bar-container"></div>;
                                                                        if (isNaN(taskStartDate.getTime()) || isNaN(taskEndDate.getTime())) return <div key={task.id} className="gantt-task-bar-container"></div>;
                                                                        const startOffset = daysBetween(ganttStartDate, taskStartDate);
                                                                        const duration = daysBetween(taskStartDate, taskEndDate) + 1;
                                                                        if (startOffset < 0 || duration <= 0) return <div key={task.id} className="gantt-task-bar-container"></div>;
                                                                        const barStyle = { backgroundColor: getTaskStatusInfo(task).color, '--start-offset': startOffset, '--duration': duration };
                                                                        return (
                                                                            <div key={task.id} className="gantt-task-bar-container">
                                                                                <div className="gantt-task-bar" style={barStyle} title={`${task.task_name} (${task.start_date} to ${task.end_date})`} />
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </React.Fragment>
                                                            ))}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                    <TaskLegend />
                                </div>
                            </div>
                        ) : (<p className="info-message">No tasks with valid dates found in the schedule.</p>)}
                    </>
                )}
            </main>
            <HistoryModal
                isOpen={isHistoryModalOpen}
                onClose={() => setIsHistoryModalOpen(false)}
                history={history}
            />
            <style>{`
                 /* --- Dark Theme Variables --- */
                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0; --light-text: #a0aec0;
                    --green-accent: #48bb78; --border-color: #4a5568; --input-bg: #1a202c;
                    --error-color: #fca5a5; --error-bg: rgba(239, 68, 68, 0.1); --danger-color: #ef4444;
                    --blue-accent: #60a5fa; --yellow-accent: #fcd34d;
                    /* Gantt Specific */
                    --gantt-bg: var(--input-bg); 
                    --gantt-header-bg: var(--card-bg); 
                    --gantt-row-border: #374151; 
                    --gantt-grid-line: var(--border-color); 
                }

                 /* --- Base Styles --- */
                .admin-page-container { background-color: var(--dark-bg); min-height: 100vh; color: var(--text-color); font-family: 'Inter', sans-serif; }
                .admin-main-content { max-width: 95%; margin: 2rem auto; padding: 0 1rem; } 
                .page-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem; padding-bottom: 1rem; border-bottom: 1px solid var(--border-color); }
                .back-button { background: var(--card-bg); color: var(--text-color); border: 1px solid var(--border-color); padding: 0.5rem 1rem; border-radius: 6px; font-weight: 600; cursor: pointer; transition: background-color 0.2s; display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0; }
                .back-button:hover { background: #4a5568; }
                .header-title-group {
                    display: flex;
                    align-items: center; 
                    flex-grow: 1;
                    flex-wrap: wrap; 
                    gap: 1rem; 
                }

                .page-title {
                    font-size: 2rem;
                    font-weight: 700;
                    color: var(--text-color);
                    margin: 0; 
                    line-height: 1; 
                    white-space: nowrap;
                }

                .project-context-inline {
                    display: flex;
                    align-items: center;
                    font-size: 1rem; 
                    color: var(--light-text);
                    font-weight: 500;
                    white-space: nowrap;
                }

                .separator {
                    margin-right: 1rem;
                    color: var(--border-color); 
                    font-weight: 300;
                    font-size: 1.2rem;
                    position: relative;
                    top: -1px; 
                }

                .context-label {
                    margin-right: 0.5rem;
                }

                .project-highlight {
                    color: var(--green-accent);
                    font-weight: 600;
                }
                .page-header-actions { display: flex; gap: 0.75rem; flex-shrink: 0; }

                 /* --- Card Style --- */
                 .content-card {
                    background-color: var(--card-bg); border: 1px solid var(--border-color);
                    border-radius: 12px; padding: 1.5rem 2rem;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                    margin-bottom: 1.5rem; 
                 }

                /* Controls & Filters */
                .controls-wrapper { display: flex; justify-content: flex-end; align-items: center; flex-wrap: wrap; }
                .view-toggle { display: inline-flex; border: 1px solid var(--border-color); border-radius: 6px; overflow: hidden; }
                .view-toggle button { padding: 0.6rem 1.2rem; border: none; background-color: var(--input-bg); font-size: 0.9rem; color: var(--light-text); cursor: pointer; transition: background-color 0.2s, color 0.2s; border-right: 1px solid var(--border-color); }
                .view-toggle button:last-child { border-right: none; }
                .view-toggle button.active { background-color: var(--green-accent); color: var(--dark-bg); font-weight: 600; }
                .view-toggle button:hover:not(.active) { background-color: var(--card-bg); }

                .filter-controls-wrapper {
                    display: flex; gap: 1.5rem; align-items: flex-end;
                    flex-wrap: wrap; 
                }
                .filter-group { display: flex; flex-direction: column; }
                .filter-group label { font-size: 0.9rem; font-weight: 500; color: var(--light-text); margin-bottom: 0.5rem; }
                .filter-group select {
                    min-width: 220px; border: 1px solid var(--border-color); border-radius: 6px;
                    padding: 0.5rem; height: 90px;
                    background-color: var(--input-bg); color: var(--text-color);
                    color-scheme: dark;
                }
                .filter-group select option { padding: 0.3rem 0.5rem; }
                .filter-group select::-webkit-scrollbar { width: 8px; }
                .filter-group select::-webkit-scrollbar-track { background: var(--input-bg); border-radius: 8px; }
                .filter-group select::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 8px; }
                .filter-group select::-webkit-scrollbar-thumb:hover { background-color: var(--light-text); }

                .filter-group select option { 
                    padding: 0.4rem 0.6rem; 
                    margin-bottom: 2px;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: background-color 0.1s, color 0.1s;
                }

                .filter-group select option:hover {
                    background-color: rgba(255, 255, 255, 0.05);
                }

                .filter-group select option.selected-filter-option {
                    background-color: rgba(72, 187, 120, 0.15); /* Faint green tint */
                    color: var(--green-accent);
                    border-left: 3px solid var(--green-accent);
                    font-weight: 600;
                }


                .action-button { padding: 0.6rem 1.2rem; border: none; border-radius: 6px; font-size: 0.9rem; font-weight: 600; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; gap: 0.5rem; }
                .action-button.secondary { background-color: var(--border-color); color: var(--text-color); }
                .action-button.secondary:hover { background-color: #5a6578; }
                .action-button.danger { background-color: var(--danger-color); color: var(--text-color); }
                .action-button.danger:hover { background-color: #dc2626; }
                .action-button:disabled { background-color: #4a5568; cursor: not-allowed; opacity: 0.7; }

                .reset-filters-button { height: 44px; width: 100px; }


                /* Gantt Chart Styles */
                .gantt-main-content { width: 100%; overflow: hidden; padding: 0; }
                .gantt-container { overflow-x: auto; background-color: var(--gantt-bg); border-radius: 8px; }
                .gantt-chart { display: grid; grid-template-columns: max-content 1fr; border: 1px solid var(--border-color); min-width: 1000px; }
                .gantt-labels { display: grid; grid-template-columns: 200px max-content; background-color: var(--gantt-bg); border-right: 1px solid var(--border-color); }
                .gantt-header-row { display: contents; }
                .gantt-header-cell {
                    background-color: var(--gantt-header-bg); position: sticky; top: 0; z-index: 20;
                    font-weight: 600; color: var(--light-text); padding: 0.75rem 1rem;
                    border-bottom: 1px solid var(--border-color); height: 57px; 
                    display: flex; align-items: center; font-size: 0.8rem; text-transform: uppercase;
                    box-sizing: border-box; 
                }
                .gantt-header-cell.responsible-header { border-left: 1px solid var(--border-color); }

                .gantt-timeline { grid-column: 2; overflow: hidden; }
                .gantt-header-timeline { display: grid; position: sticky; top: 0; z-index: 20; background-color: var(--gantt-header-bg); border-bottom: 1px solid var(--border-color); }
                .gantt-date-cell {
                    text-align: center; font-size: 0.75rem; border-left: 1px solid var(--gantt-grid-line);
                    display: flex; flex-direction: column; justify-content: center;
                    color: var(--light-text); padding: 0.5rem 0; height: 56px; 
                    box-sizing: border-box; overflow: hidden;
                }
                .gantt-date-cell .date-month { font-size: 0.7rem; }
                .gantt-date-cell .date-day { font-weight: 600; font-size: 0.9rem; color: var(--text-color); }
                .gantt-date-cell .week-label { font-weight: 500; font-size: 0.8rem; color: var(--text-color); padding: 0 0.2rem; }
                .gantt-date-cell .month-label { font-weight: 600; font-size: 0.9rem; color: var(--text-color); }
                .gantt-body { display: contents; }

                .gantt-drop-header-row { display: contents; }
                .gantt-drop-header {
                    background-color: var(--input-bg); font-weight: 700; color: var(--text-color);
                    padding: 0 1rem; border-top: 1px solid var(--border-color);
                    border-bottom: 1px solid var(--border-color); height: 44px; cursor: pointer;
                    display: flex; align-items: center; grid-column: 1 / 3;
                    box-sizing: border-box; 
                }
                .collapse-arrow { font-size: 0.8em; margin-right: 0.5rem; transition: transform 0.2s ease-in-out; color: var(--light-text); }
                .collapse-arrow.collapsed { transform: rotate(-90deg); }

                .gantt-interface-header-row { display: contents; }
                .gantt-interface-header {
                    background-color: var(--card-bg); font-weight: 600; color: var(--light-text);
                    padding: 0 1rem 0 1.5rem; border-top: 1px solid var(--gantt-row-border);
                    border-bottom: 1px solid var(--gantt-row-border); height: 44px; 
                    display:flex; align-items:center; grid-column: 1 / 3; font-size: 0.9rem;
                    box-sizing: border-box; 
                }
                
                .interface-header-content {
                    display: flex;
                    justify-content: space-between; 
                    align-items: center;
                    width: 100%; 
                }
                
                .minimize-interface-btn {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    background: none;
                    border: 1px solid var(--border-color);
                    color: var(--light-text);
                    padding: 0.2rem 0.4rem; 
                    border-radius: 4px;
                    cursor: pointer;
                    line-height: 1; 
                    font-size: 0.8rem; 
                    transition: background-color 0.2s, color 0.2s;
                }
                .minimize-interface-btn:hover {
                    background-color: var(--border-color);
                    color: var(--text-color);
                }

                .gantt-task-row-labels { display: contents; }
                .gantt-task-label {
                    border-bottom: 1px solid var(--gantt-row-border);
                    font-size: 0.8rem; color: var(--light-text); display: flex;
                    align-items: center; height: 39px; grid-column: 1;
                    padding: 0 1rem 0 2rem; 
                    box-sizing: border-box; 
                }
                .task-name-detail { font-weight: 500; color: var(--text-color); font-size: 0.875rem; }
                .gantt-responsible-label {
                    border-bottom: 1px solid var(--gantt-row-border); border-left: 1px solid var(--border-color);
                    height: 39px; display: flex; align-items: center; justify-content: flex-start;
                    padding: 0 1rem; grid-column: 2;
                    box-sizing: border-box; 
                }
                .task-responsible-text { font-size: 0.875rem; font-weight: 500; color: var(--text-color); white-space: nowrap; padding-right: 1rem;}

                .gantt-row { 
                     position: relative;
                     border-bottom: 1px solid var(--gantt-row-border); 
                     background-image: linear-gradient(to right, var(--gantt-grid-line) 1px, transparent 1px);
                     background-size: calc(100% / var(--total-units)) 100%;
                     box-sizing: border-box; 
                 }
                .gantt-drop-separator {
                    height: 44px; 
                    background-color: var(--input-bg);
                    border-top: 1px solid var(--border-color);
                    border-bottom: 1px solid var(--border-color);
                    position: relative;
                    box-sizing: border-box;
                }
                .gantt-interface-separator {
                    height: 44px; 
                    background-color: var(--card-bg);
                    border-bottom: 1px solid var(--gantt-row-border); 
                    position: relative;
                    box-sizing: border-box;
                }
                .gantt-task-bar-container {
                    height: 39px; 
                    border-bottom: 1px solid var(--gantt-row-border);
                    position: relative;
                    box-sizing: border-box;
                }

                 /* Gantt Bars */
                .gantt-summary-bar, .gantt-task-bar {
                    position: absolute; left: calc(var(--start-offset, 0) * 100% / var(--total-units, 1));
                    width: calc(var(--duration, 0) * 100% / var(--total-units, 1));
                    border-radius: 4px; opacity: 0.9; box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                    border: 1px solid rgba(255,255,255,0.1);
                    min-width: 2px;
                }
                .gantt-drop-bar { top: 10px; height: 24px; z-index: 5; opacity: 0.6; } 
                .gantt-interface-bar { top: 10px; height: 24px; z-index: 10; opacity: 0.7; } 

                .gantt-task-bar {
                    top: 4px; 
                    height: calc(100% - 8px); 
                    z-index: 15;
                    display: flex; align-items: center; overflow: hidden;
                    box-sizing: border-box; 
                }

                /* Task Legend */
                .task-legend {
                    display: flex; flex-wrap: wrap; gap: 0.5rem 1.5rem;
                    justify-content: center; 
                    margin: 0; 
                    padding: 1rem 1.5rem; 
                    border-top: 1px solid var(--border-color);
                    background-color: var(--gantt-bg); 
                    font-size: 0.85rem; 
                    color: var(--light-text);
                 }
                .legend-item { display: flex; align-items: center; }
                .legend-color-box { height: 0.8rem; width: 0.8rem; border-radius: 3px; margin-right: 0.5rem; border: 1px solid rgba(255,255,255,0.1); }
                .summary-box { opacity: 0.7; }

                /* History Modal */
                .modal-overlay { 
                    z-index: 1050; 
                    position: fixed; 
                    top: 0; left: 0; right: 0; bottom: 0; 
                    background: rgba(0, 0, 0, 0.7); 
                    display: flex; 
                    justify-content: center; 
                    align-items: center; 
                }

                .modal-content.large-modal { 
                    background: var(--card-bg); 
                    border: 1px solid var(--border-color); 
                    border-radius: 12px; 
                    width: 90%; 
                    max-width: 700px; 
                    max-height: 90vh; 
                    display: flex; 
                    flex-direction: column; 
                    box-shadow: 0 10px 25px rgba(0,0,0,0.5); 
                }

                .modal-header { 
                    display: flex; justify-content: space-between; align-items: center; 
                    padding: 1.25rem 1.5rem; 
                    border-bottom: 1px solid var(--border-color); 
                }
                .modal-header h3 { font-size: 1.25rem; font-weight: 600; color: var(--text-color); margin: 0; }
                .modal-close-button { 
                    background: transparent; border: none; font-size: 1.5rem; 
                    cursor: pointer; color: var(--light-text); transition: color 0.2s; 
                }
                .modal-close-button:hover { color: var(--text-color); }

                .history-list { 
                    list-style: none; padding: 1rem 1.5rem; max-height: 60vh; 
                    overflow-y: auto; margin: 0; 
                }
                .history-list::-webkit-scrollbar { width: 8px; }
                .history-list::-webkit-scrollbar-track { background: var(--input-bg); }
                .history-list::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 8px; }
                .history-list::-webkit-scrollbar-thumb:hover { background-color: var(--light-text); }

                .history-item { padding: 0.75rem 0; border-bottom: 1px solid var(--border-color); }
                .history-item:last-child { border-bottom: none; }
                .history-meta { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.35rem; }
                .history-user { font-weight: 600; color: var(--blue-accent); font-size: 0.9rem; }
                .history-timestamp { font-size: 0.8rem; color: var(--light-text); }
                .history-description { color: var(--text-color); font-size: 0.95rem; margin: 0; line-height: 1.4; }

                .modal-footer { 
                    display: flex; justify-content: flex-end; 
                    padding: 1rem 1.5rem; 
                    border-top: 1px solid var(--border-color); 
                }

                /* Utility & Message Classes */
                 .loading-container { display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 3rem; color: var(--light-text); }
                 .loading-spinner { width: 40px; height: 40px; margin: 0 auto 1rem; border: 4px solid var(--border-color); border-top-color: var(--green-accent); border-radius: 50%; animation: spin 1s linear infinite; }
                 @keyframes spin { to { transform: rotate(360deg); } }
                 .form-feedback.error { background-color: var(--error-bg); color: var(--error-color); padding: 1rem; border-radius: 8px; text-align: center; border: 1px solid var(--danger-color); margin-bottom: 1.5rem; }
                 .info-message {
                     background-color: var(--card-bg); border: 1px solid var(--border-color);
                     color: var(--light-text); padding: 1.5rem; border-radius: 8px;
                     text-align: center; margin-top: 2rem;
                 }

                 select { color-scheme: dark; }

                 @media (max-width: 768px) {
                    .admin-main-content { padding: 0 0.5rem; } 
                    .page-header { flex-wrap: wrap; }
                    .page-header-actions { width: 100%; justify-content: center; margin-top: 0.5rem; }
                    .controls-wrapper { flex-direction: column; align-items: stretch; gap: 1rem; }
                    .view-toggle { margin-left: 0; width: 100%; display: flex; }
                    .view-toggle button { flex-grow: 1; }
                    .filter-controls-wrapper { flex-direction: column; align-items: stretch; }
                    .filter-group select { min-width: unset; width: 100%; }
                    .gantt-chart { grid-template-columns: 240px 1fr; }
                    .gantt-labels { grid-template-columns: 140px 100px; }
                 }

            `}</style>
        </div>
    );
}